import { useRef, useState } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import useZebraRowColors from 'hooks/useZebraRowColors';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import GiftOutlined from '@ant-design/icons/GiftOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',
  transform: 'none'
};

const NOTIFICATION_ITEMS = [
  {
    id: 'birthday',
    unreadWhen: true,
    time: '3:00 AM',
    secondary: '2 min ago',
    avatar: (
      <Avatar sx={{ color: 'success.main', bgcolor: 'success.lighter' }}>
        <GiftOutlined />
      </Avatar>
    ),
    primary: (
      <Typography variant="h6">
        It&apos;s{' '}
        <Typography component="span" variant="subtitle1">
          Cristina danny&apos;s
        </Typography>{' '}
        birthday today.
      </Typography>
    ),
  },
  {
    id: 'comment',
    unreadWhen: false,
    time: '6:00 AM',
    secondary: '5 August',
    avatar: (
      <Avatar sx={{ color: 'primary.main', bgcolor: 'primary.lighter' }}>
        <MessageOutlined />
      </Avatar>
    ),
    primary: (
      <Typography variant="h6">
        <Typography component="span" variant="subtitle1">
          Aida Burg
        </Typography>{' '}
        commented your post.
      </Typography>
    ),
  },
  {
    id: 'profile',
    unreadWhen: true,
    time: '2:45 PM',
    secondary: '7 hours ago',
    avatar: (
      <Avatar sx={{ color: 'error.main', bgcolor: 'error.lighter' }}>
        <SettingOutlined />
      </Avatar>
    ),
    primary: (
      <Typography variant="h6">
        Your Profile is Complete &nbsp;
        <Typography component="span" variant="subtitle1">
          60%
        </Typography>{' '}
      </Typography>
    ),
  },
  {
    id: 'meeting',
    unreadWhen: false,
    time: '9:10 PM',
    secondary: 'Daily scrum meeting time',
    avatar: (
      <Avatar sx={{ color: 'primary.main', bgcolor: 'primary.lighter' }}>C</Avatar>
    ),
    primary: (
      <Typography variant="h6">
        <Typography component="span" variant="subtitle1">
          Cristina Danny
        </Typography>{' '}
        invited to join{' '}
        <Typography component="span" variant="subtitle1">
          Meeting.
        </Typography>
      </Typography>
    ),
  },
];

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const { theme, stripeBg, isDarkUi } = useZebraRowColors();

  const anchorRef = useRef(null);
  const [read, setRead] = useState(2);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const listItemStripeSx = (index) => ({
    bgcolor: stripeBg(index),
    ...(isDarkUi
      ? {
          borderRadius: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }
      : {}),
    '&.Mui-selected': {
      bgcolor: isDarkUi
        ? alpha(theme.palette.primary.main, 0.16)
        : alpha(theme.palette.primary.main, 0.08),
      color: 'text.primary',
    },
    '&.Mui-selected:hover': {
      bgcolor: isDarkUi
        ? alpha(theme.palette.primary.main, 0.22)
        : alpha(theme.palette.primary.main, 0.12),
    },
  });

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={{
          color: 'text.primary',
          bgcolor: open
            ? isDarkUi
              ? alpha(theme.palette.primary.main, 0.12)
              : 'grey.100'
            : 'transparent',
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={read} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notification"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <>
                      {read > 0 && (
                        <Tooltip title="Mark as all read">
                          <IconButton color="success" size="small" onClick={() => setRead(0)}>
                            <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      '& .MuiListItemButton-root': {
                        py: 0.5,
                        px: 2,
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' },
                      },
                    }}
                  >
                    {NOTIFICATION_ITEMS.map((item, index) => (
                      <ListItem
                        key={item.id}
                        component={ListItemButton}
                        divider={!isDarkUi}
                        selected={item.unreadWhen && read > 0}
                        sx={listItemStripeSx(index)}
                        secondaryAction={
                          <Typography variant="caption" noWrap>
                            {item.time}
                          </Typography>
                        }
                      >
                        <ListItemAvatar>{item.avatar}</ListItemAvatar>
                        <ListItemText primary={item.primary} secondary={item.secondary} />
                      </ListItem>
                    ))}
                    <ListItemButton sx={{ textAlign: 'center', py: `${12}px !important` }}>
                      <ListItemText
                        primary={
                          <Typography variant="h6" color="primary">
                            View All
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
